import React, { useState, useRef, useCallback } from 'react';
import { Database, FileText, Mic, Download, Trash2, Upload, Shield, Clock, AlertTriangle, CheckCircle, UploadCloud, X, MicOff, Square } from 'lucide-react';
import DataPermissionsView from './DataPermissionsView';
import AuditLogViewer from './AuditLogViewer';

interface DataItem {
    id: string;
    name: string;
    type: 'document' | 'voice';
    size: string;
    uploadedAt: string;
    status: 'processed' | 'processing' | 'failed';
}

interface MyDataViewProps {
    onNavigate?: (view: string) => void;
}

function formatFileSize(bytes: number): string {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function formatDate(date: Date): string {
    return date.toISOString().split('T')[0];
}

const MyDataView: React.FC<MyDataViewProps> = ({ onNavigate }) => {
    const [activeTab, setActiveTab] = useState<'training' | 'permissions' | 'audit'>('training');
    const [documents, setDocuments] = useState<DataItem[]>([]);
    const [voiceSamples, setVoiceSamples] = useState<DataItem[]>([]);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
    const [exporting, setExporting] = useState(false);
    const [dragOverDocs, setDragOverDocs] = useState(false);
    const [dragOverVoice, setDragOverVoice] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);

    const docInputRef = useRef<HTMLInputElement>(null);
    const voiceInputRef = useRef<HTMLInputElement>(null);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const audioChunksRef = useRef<Blob[]>([]);
    const recordingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    // Simulate processing (in production: upload to backend → RAG pipeline)
    const simulateProcessing = useCallback((id: string, type: 'document' | 'voice') => {
        setTimeout(() => {
            const setter = type === 'document' ? setDocuments : setVoiceSamples;
            setter(prev => prev.map(item =>
                item.id === id ? { ...item, status: 'processed' as const } : item
            ));
        }, 2000 + Math.random() * 2000);
    }, []);

    // Handle document file selection
    const handleDocumentFiles = useCallback((files: FileList | File[]) => {
        const acceptedTypes = [
            'application/pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'text/plain',
            'text/csv',
            'text/markdown',
            'application/json',
        ];
        const acceptedExtensions = ['.pdf', '.doc', '.docx', '.txt', '.csv', '.md', '.json'];

        const newDocs: DataItem[] = [];

        Array.from(files).forEach(file => {
            const ext = '.' + file.name.split('.').pop()?.toLowerCase();
            const isAccepted = acceptedTypes.includes(file.type) || acceptedExtensions.includes(ext);

            if (!isAccepted) return; // Skip unsupported files silently

            const id = crypto.randomUUID();
            newDocs.push({
                id,
                name: file.name,
                type: 'document',
                size: formatFileSize(file.size),
                uploadedAt: formatDate(new Date()),
                status: 'processing',
            });

            simulateProcessing(id, 'document');
        });

        if (newDocs.length > 0) {
            setDocuments(prev => [...prev, ...newDocs]);
        }
    }, [simulateProcessing]);

    // Handle voice file selection
    const handleVoiceFiles = useCallback((files: FileList | File[]) => {
        const acceptedTypes = ['audio/wav', 'audio/mpeg', 'audio/mp3', 'audio/ogg', 'audio/webm', 'audio/mp4', 'audio/x-m4a'];
        const acceptedExtensions = ['.wav', '.mp3', '.ogg', '.webm', '.m4a', '.aac'];

        const newSamples: DataItem[] = [];

        Array.from(files).forEach(file => {
            const ext = '.' + file.name.split('.').pop()?.toLowerCase();
            const isAccepted = acceptedTypes.includes(file.type) || acceptedExtensions.includes(ext);

            if (!isAccepted) return;

            const id = crypto.randomUUID();
            newSamples.push({
                id,
                name: file.name,
                type: 'voice',
                size: formatFileSize(file.size),
                uploadedAt: formatDate(new Date()),
                status: 'processing',
            });

            simulateProcessing(id, 'voice');
        });

        if (newSamples.length > 0) {
            setVoiceSamples(prev => [...prev, ...newSamples]);
        }
    }, [simulateProcessing]);

    // Drag & drop handlers
    const handleDragOver = (e: React.DragEvent, setter: (v: boolean) => void) => {
        e.preventDefault();
        e.stopPropagation();
        setter(true);
    };

    const handleDragLeave = (e: React.DragEvent, setter: (v: boolean) => void) => {
        e.preventDefault();
        e.stopPropagation();
        setter(false);
    };

    const handleDropDocs = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOverDocs(false);
        if (e.dataTransfer.files.length > 0) {
            handleDocumentFiles(e.dataTransfer.files);
        }
    };

    const handleDropVoice = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOverVoice(false);
        if (e.dataTransfer.files.length > 0) {
            handleVoiceFiles(e.dataTransfer.files);
        }
    };

    // Voice recording
    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mediaRecorder = new MediaRecorder(stream);
            mediaRecorderRef.current = mediaRecorder;
            audioChunksRef.current = [];

            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };

            mediaRecorder.onstop = () => {
                const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                const file = new File([audioBlob], `voice_recording_${Date.now()}.webm`, { type: 'audio/webm' });
                handleVoiceFiles([file]);
                stream.getTracks().forEach(track => track.stop());
            };

            mediaRecorder.start();
            setIsRecording(true);
            setRecordingTime(0);
            recordingIntervalRef.current = setInterval(() => {
                setRecordingTime(prev => prev + 1);
            }, 1000);
        } catch {
            // Microphone access denied
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            mediaRecorderRef.current.stop();
        }
        setIsRecording(false);
        if (recordingIntervalRef.current) {
            clearInterval(recordingIntervalRef.current);
            recordingIntervalRef.current = null;
        }
    };

    const formatRecordingTime = (seconds: number) => {
        const m = Math.floor(seconds / 60).toString().padStart(2, '0');
        const s = (seconds % 60).toString().padStart(2, '0');
        return `${m}:${s}`;
    };

    const handleDelete = (id: string, type: 'document' | 'voice') => {
        if (type === 'document') {
            setDocuments(prev => prev.filter(d => d.id !== id));
        } else {
            setVoiceSamples(prev => prev.filter(v => v.id !== id));
        }
        setShowDeleteConfirm(null);
    };

    const handleExportAll = async () => {
        setExporting(true);
        await new Promise(resolve => setTimeout(resolve, 2000));
        setExporting(false);
        alert('Your data export has been initiated. You will receive an email when ready.');
    };

    const getStatusBadge = (status: DataItem['status']) => {
        switch (status) {
            case 'processed':
                return (
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-hig-success bg-hig-success/10 px-2.5 py-1 rounded-full">
                        <CheckCircle size={12} /> Processed
                    </span>
                );
            case 'processing':
                return (
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-hig-warning bg-hig-warning/10 px-2.5 py-1 rounded-full">
                        <Clock size={12} className="animate-spin" /> Processing...
                    </span>
                );
            case 'failed':
                return (
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-hig-error bg-hig-error/10 px-2.5 py-1 rounded-full">
                        <AlertTriangle size={12} /> Failed
                    </span>
                );
        }
    };

    // Training completeness calculation
    const docScore = Math.min(documents.filter(d => d.status === 'processed').length * 15, 40);
    const voiceScore = Math.min(voiceSamples.filter(v => v.status === 'processed').length * 15, 30);
    const totalItems = documents.length + voiceSamples.length;
    const baseScore = totalItems > 0 ? 10 : 0;
    const trainingCompleteness = Math.min(docScore + voiceScore + baseScore, 100);

    return (
        <div className="max-w-6xl mx-auto space-y-8 lg:space-y-10 pb-12">
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center">
                            <Database size={22} className="text-purple-400" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-hig-text-primary tracking-tight">
                                Memory & Access
                            </h1>
                            <p className="text-xs text-hig-text-tertiary uppercase tracking-widest font-bold">Sovereignty Mode</p>
                        </div>
                    </div>
                    <p className="text-hig-text-secondary mt-2 text-sm">
                        Manage memory layers, training data, and data sovereignty boundaries
                    </p>
                </div>
                <button
                    onClick={handleExportAll}
                    disabled={exporting || totalItems === 0}
                    className="btn-secondary flex items-center gap-2 disabled:opacity-40"
                >
                    <Download size={16} />
                    {exporting ? 'Exporting...' : 'Export All Data (GDPR)'}
                </button>
            </div>

            {/* Quick-nav Tabs */}
            <div className="flex gap-2">
                <button 
                    onClick={() => setActiveTab('training')}
                    className={`px-4 py-2 rounded-lg text-sm transition-colors ${activeTab === 'training' ? 'font-semibold bg-purple-500/10 text-purple-400 border border-purple-500/20' : 'font-medium text-hig-text-secondary hover:text-hig-text-primary hover:bg-hig-bg-tertiary border border-transparent'} `}
                >
                    Training Data
                </button>
                <button
                    onClick={() => setActiveTab('permissions')}
                    className={`px-4 py-2 rounded-lg text-sm transition-colors ${activeTab === 'permissions' ? 'font-semibold bg-purple-500/10 text-purple-400 border border-purple-500/20' : 'font-medium text-hig-text-secondary hover:text-hig-text-primary hover:bg-hig-bg-tertiary border border-transparent'} `}
                >
                    Permissions
                </button>
                <button
                    onClick={() => setActiveTab('audit')}
                    className={`px-4 py-2 rounded-lg text-sm transition-colors ${activeTab === 'audit' ? 'font-semibold bg-purple-500/10 text-purple-400 border border-purple-500/20' : 'font-medium text-hig-text-secondary hover:text-hig-text-primary hover:bg-hig-bg-tertiary border border-transparent'} `}
                >
                    Audit Logs
                </button>
            </div>

            {activeTab === 'training' && (
                <>
            {/* Training Completeness */}
            <div className="card p-7 lg:p-8">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-hig-text-primary text-lg">Memory Calibration</h3>
                    <span className={`text-2xl font-bold ${trainingCompleteness >= 70 ? 'text-hig-success' : trainingCompleteness >= 30 ? 'text-hig-warning' : 'text-hig-text-tertiary'}`}>
                        {trainingCompleteness}%
                    </span>
                </div>
                <div className="w-full h-3 bg-hig-bg-tertiary rounded-full overflow-hidden">
                    <div
                        className={`h-full rounded-full transition-all duration-700 ${trainingCompleteness >= 70 ? 'bg-gradient-to-r from-hig-success to-emerald-400' : trainingCompleteness >= 30 ? 'bg-gradient-to-r from-hig-warning to-amber-400' : 'bg-hig-bg-tertiary'}`}
                        style={{ width: `${trainingCompleteness}%` }}
                    />
                </div>
                <div className="flex justify-between mt-4 text-sm text-hig-text-tertiary">
                    <span>Documents: {documents.filter(d => d.status === 'processed').length} processed</span>
                    <span>Voice: {voiceSamples.filter(v => v.status === 'processed').length} samples</span>
                    <span>{totalItems === 0 ? 'Upload files to get started' : `${totalItems} total files`}</span>
                </div>
            </div>

            {/* Documents Section */}
            <div className="card overflow-hidden">
                <div className="p-6 lg:p-7 border-b border-hig-divider flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <FileText size={20} className="text-hig-accent" />
                        <h2 className="font-bold text-hig-text-primary text-lg">Training Documents</h2>
                        {documents.length > 0 && (
                            <span className="text-sm text-hig-text-tertiary font-medium">({documents.length} files)</span>
                        )}
                    </div>
                    <button
                        onClick={() => docInputRef.current?.click()}
                        className="btn-primary text-sm flex items-center gap-2"
                    >
                        <Upload size={14} />
                        Upload
                    </button>
                    <input
                        ref={docInputRef}
                        type="file"
                        multiple
                        accept=".pdf,.doc,.docx,.txt,.csv,.md,.json"
                        className="hidden"
                        onChange={(e) => e.target.files && handleDocumentFiles(e.target.files)}
                    />
                </div>

                {documents.length === 0 ? (
                    /* Drop zone - Empty state */
                    <div
                        onDragOver={(e) => handleDragOver(e, setDragOverDocs)}
                        onDragLeave={(e) => handleDragLeave(e, setDragOverDocs)}
                        onDrop={handleDropDocs}
                        onClick={() => docInputRef.current?.click()}
                        className={`p-12 lg:p-16 text-center cursor-pointer transition-all duration-300 ${dragOverDocs
                            ? 'bg-hig-accent/10 border-2 border-dashed border-hig-accent'
                            : 'hover:bg-hig-bg-tertiary/30'
                            }`}
                    >
                        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5 transition-all duration-300 ${dragOverDocs
                            ? 'bg-hig-accent/20 scale-110'
                            : 'bg-hig-bg-tertiary'
                            }`}>
                            <UploadCloud size={32} className={dragOverDocs ? 'text-hig-accent' : 'text-hig-text-tertiary'} />
                        </div>
                        <p className="text-hig-text-primary font-semibold text-base mb-2">
                            {dragOverDocs ? 'Drop your files here' : 'Drag & drop your documents here'}
                        </p>
                        <p className="text-sm text-hig-text-tertiary mb-4">
                            or click to browse your files
                        </p>
                        <div className="flex flex-wrap items-center justify-center gap-2">
                            {['PDF', 'DOCX', 'TXT', 'CSV', 'MD', 'JSON'].map(ext => (
                                <span key={ext} className="text-xs px-2.5 py-1 rounded-lg bg-hig-bg-tertiary text-hig-text-tertiary font-medium">
                                    .{ext.toLowerCase()}
                                </span>
                            ))}
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Drop zone - Compact when files exist */}
                        <div
                            onDragOver={(e) => handleDragOver(e, setDragOverDocs)}
                            onDragLeave={(e) => handleDragLeave(e, setDragOverDocs)}
                            onDrop={handleDropDocs}
                            onClick={() => docInputRef.current?.click()}
                            className={`mx-6 mt-5 mb-2 p-5 rounded-xl border-2 border-dashed text-center cursor-pointer transition-all duration-300 ${dragOverDocs
                                ? 'border-hig-accent bg-hig-accent/10'
                                : 'border-hig-divider hover:border-hig-text-tertiary hover:bg-hig-bg-tertiary/30'
                                }`}
                        >
                            <div className="flex items-center justify-center gap-3">
                                <UploadCloud size={20} className={dragOverDocs ? 'text-hig-accent' : 'text-hig-text-tertiary'} />
                                <span className="text-sm text-hig-text-secondary font-medium">
                                    {dragOverDocs ? 'Drop files here' : 'Drop more files or click to upload'}
                                </span>
                            </div>
                        </div>

                        {/* File list */}
                        <div className="divide-y divide-hig-divider mx-1">
                            {documents.map((doc) => (
                                <div key={doc.id} className="px-6 py-4 flex items-center justify-between hover:bg-hig-bg-tertiary/30 transition-colors duration-200">
                                    <div className="flex items-center gap-4">
                                        <div className="w-11 h-11 rounded-xl bg-hig-accent/10 flex items-center justify-center flex-shrink-0">
                                            <FileText size={20} className="text-hig-accent" />
                                        </div>
                                        <div>
                                            <p className="font-semibold text-hig-text-primary">{doc.name}</p>
                                            <p className="text-xs text-hig-text-tertiary mt-0.5">{doc.size} &middot; Uploaded {doc.uploadedAt}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        {getStatusBadge(doc.status)}
                                        <button
                                            onClick={() => setShowDeleteConfirm(doc.id)}
                                            className="p-2.5 text-hig-text-tertiary hover:text-hig-error hover:bg-hig-error/10 rounded-lg transition-colors"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>

            {/* Voice Samples Section */}
            <div className="card overflow-hidden">
                <div className="p-6 lg:p-7 border-b border-hig-divider flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Mic size={20} className="text-hig-accent" />
                        <h2 className="font-bold text-hig-text-primary text-lg">Voice Samples</h2>
                        {voiceSamples.length > 0 && (
                            <span className="text-sm text-hig-text-tertiary font-medium">({voiceSamples.length} recordings)</span>
                        )}
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => voiceInputRef.current?.click()}
                            className="btn-secondary text-sm flex items-center gap-2"
                        >
                            <Upload size={14} />
                            Upload
                        </button>
                        {isRecording ? (
                            <button
                                onClick={stopRecording}
                                className="flex items-center gap-2 px-4 py-2 bg-hig-error text-white rounded-xl text-sm font-semibold hover:bg-hig-error/90 transition-colors"
                            >
                                <Square size={14} />
                                Stop {formatRecordingTime(recordingTime)}
                            </button>
                        ) : (
                            <button
                                onClick={startRecording}
                                className="btn-primary text-sm flex items-center gap-2"
                            >
                                <Mic size={14} />
                                Record
                            </button>
                        )}
                    </div>
                    <input
                        ref={voiceInputRef}
                        type="file"
                        multiple
                        accept=".wav,.mp3,.ogg,.webm,.m4a,.aac"
                        className="hidden"
                        onChange={(e) => e.target.files && handleVoiceFiles(e.target.files)}
                    />
                </div>

                {/* Recording indicator */}
                {isRecording && (
                    <div className="mx-6 mt-5 p-5 rounded-xl bg-hig-error/10 border border-hig-error/20 flex items-center gap-4">
                        <div className="w-4 h-4 rounded-full bg-hig-error animate-pulse flex-shrink-0" />
                        <div>
                            <p className="font-semibold text-hig-text-primary text-sm">Recording in progress...</p>
                            <p className="text-xs text-hig-text-secondary mt-0.5">
                                {formatRecordingTime(recordingTime)} — Click "Stop" when finished
                            </p>
                        </div>
                    </div>
                )}

                {voiceSamples.length === 0 && !isRecording ? (
                    /* Drop zone - Empty state */
                    <div
                        onDragOver={(e) => handleDragOver(e, setDragOverVoice)}
                        onDragLeave={(e) => handleDragLeave(e, setDragOverVoice)}
                        onDrop={handleDropVoice}
                        onClick={() => voiceInputRef.current?.click()}
                        className={`p-12 lg:p-16 text-center cursor-pointer transition-all duration-300 ${dragOverVoice
                            ? 'bg-hig-accent/10 border-2 border-dashed border-hig-accent'
                            : 'hover:bg-hig-bg-tertiary/30'
                            }`}
                    >
                        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5 transition-all duration-300 ${dragOverVoice
                            ? 'bg-hig-accent/20 scale-110'
                            : 'bg-hig-bg-tertiary'
                            }`}>
                            <Mic size={32} className={dragOverVoice ? 'text-hig-accent' : 'text-hig-text-tertiary'} />
                        </div>
                        <p className="text-hig-text-primary font-semibold text-base mb-2">
                            {dragOverVoice ? 'Drop your audio files here' : 'Drag & drop voice samples or record live'}
                        </p>
                        <p className="text-sm text-hig-text-tertiary mb-4">
                            Upload audio files or use the Record button
                        </p>
                        <div className="flex flex-wrap items-center justify-center gap-2">
                            {['WAV', 'MP3', 'OGG', 'WEBM', 'M4A'].map(ext => (
                                <span key={ext} className="text-xs px-2.5 py-1 rounded-lg bg-hig-bg-tertiary text-hig-text-tertiary font-medium">
                                    .{ext.toLowerCase()}
                                </span>
                            ))}
                        </div>
                    </div>
                ) : voiceSamples.length > 0 ? (
                    <>
                        {/* Drop zone - Compact */}
                        <div
                            onDragOver={(e) => handleDragOver(e, setDragOverVoice)}
                            onDragLeave={(e) => handleDragLeave(e, setDragOverVoice)}
                            onDrop={handleDropVoice}
                            onClick={() => voiceInputRef.current?.click()}
                            className={`mx-6 mt-5 mb-2 p-5 rounded-xl border-2 border-dashed text-center cursor-pointer transition-all duration-300 ${dragOverVoice
                                ? 'border-hig-accent bg-hig-accent/10'
                                : 'border-hig-divider hover:border-hig-text-tertiary hover:bg-hig-bg-tertiary/30'
                                }`}
                        >
                            <div className="flex items-center justify-center gap-3">
                                <UploadCloud size={20} className={dragOverVoice ? 'text-hig-accent' : 'text-hig-text-tertiary'} />
                                <span className="text-sm text-hig-text-secondary font-medium">
                                    {dragOverVoice ? 'Drop audio files here' : 'Drop more audio files or click to upload'}
                                </span>
                            </div>
                        </div>

                        {/* File list */}
                        <div className="divide-y divide-hig-divider mx-1">
                            {voiceSamples.map((sample) => (
                                <div key={sample.id} className="px-6 py-4 flex items-center justify-between hover:bg-hig-bg-tertiary/30 transition-colors duration-200">
                                    <div className="flex items-center gap-4">
                                        <div className="w-11 h-11 rounded-xl bg-purple-500/10 flex items-center justify-center flex-shrink-0">
                                            <Mic size={20} className="text-purple-500" />
                                        </div>
                                        <div>
                                            <p className="font-semibold text-hig-text-primary">{sample.name}</p>
                                            <p className="text-xs text-hig-text-tertiary mt-0.5">{sample.size} &middot; Recorded {sample.uploadedAt}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        {getStatusBadge(sample.status)}
                                        <button
                                            onClick={() => setShowDeleteConfirm(sample.id)}
                                            className="p-2.5 text-hig-text-tertiary hover:text-hig-error hover:bg-hig-error/10 rounded-lg transition-colors"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                ) : null}
            </div>

            {/* GDPR Actions */}
            <div className="card p-7 lg:p-8">
                <div className="flex items-center gap-3 mb-6">
                    <Shield size={20} className="text-hig-accent" />
                    <h3 className="font-bold text-hig-text-primary text-lg">Data Sovereignty & Rights</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    <button className="p-6 bg-hig-bg-tertiary/50 rounded-2xl text-left hover:bg-hig-bg-tertiary transition-all duration-200 group">
                        <Download size={22} className="text-hig-accent mb-3 group-hover:scale-105 transition-transform" />
                        <p className="font-semibold text-hig-text-primary mb-1">Export Data</p>
                        <p className="text-xs text-hig-text-tertiary leading-relaxed">Download all your data in a portable format</p>
                    </button>
                    <button className="p-6 bg-hig-bg-tertiary/50 rounded-2xl text-left hover:bg-hig-error/5 transition-all duration-200 group">
                        <Trash2 size={22} className="text-hig-error mb-3 group-hover:scale-105 transition-transform" />
                        <p className="font-semibold text-hig-text-primary mb-1">Delete Account</p>
                        <p className="text-xs text-hig-text-tertiary leading-relaxed">Right to be forgotten — erases all data including vectors</p>
                    </button>
                    <button className="p-6 bg-hig-bg-tertiary/50 rounded-2xl text-left hover:bg-hig-bg-tertiary transition-all duration-200 group">
                        <Shield size={22} className="text-hig-success mb-3 group-hover:scale-105 transition-transform" />
                        <p className="font-semibold text-hig-text-primary mb-1">Privacy Settings</p>
                        <p className="text-xs text-hig-text-tertiary leading-relaxed">Manage consent and data processing preferences</p>
                    </button>
                </div>
            </div>
                </>
            )}

            {activeTab === 'permissions' && (
                <div className="pt-4 animate-in fade-in duration-300">
                    <DataPermissionsView />
                </div>
            )}

            {activeTab === 'audit' && (
                <div className="pt-4 animate-in fade-in duration-300">
                    <AuditLogViewer />
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="card p-8 max-w-md mx-4 shadow-2xl">
                        <h3 className="text-xl font-bold text-hig-text-primary mb-3">Delete this file?</h3>
                        <p className="text-sm text-hig-text-secondary mb-6 leading-relaxed">
                            This action cannot be undone. The file and its associated training data (including vector embeddings) will be permanently removed.
                        </p>
                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => setShowDeleteConfirm(null)}
                                className="btn-secondary px-6"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => handleDelete(showDeleteConfirm, documents.find(d => d.id === showDeleteConfirm) ? 'document' : 'voice')}
                                className="px-6 py-2.5 bg-hig-error text-white rounded-xl font-semibold hover:bg-hig-error/90 transition-colors"
                            >
                                Delete Permanently
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MyDataView;
